import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import {
  Form, Button, Badge, Card, Spinner,
} from 'react-bootstrap';
import Datetime from 'react-datetime';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import AddressAutocomplete from '../../../Components/AddressAutocomplete';
import { getRoute } from '../../../Services/MapboxService';
import { DEFAULT_TIMEZONE, localToUtc, utcToLocal } from '../../../Utils/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

const STOP_TYPES = [
  { value: 'break', label: 'Break', color: 'warning' },
  { value: 'activity', label: 'Activity', color: 'primary' },
];

function ItineraryStopsEditor({
  stops = [],
  onChange,
  disabled = false,
  startDateTime = null,
  timezone = DEFAULT_TIMEZONE,
  fromCoords = null,
  // toCoords reserved for future use (e.g., calculating total trip time)
  // eslint-disable-next-line no-unused-vars
  toCoords = null,
}) {
  const [isEstimating, setIsEstimating] = useState(false);

  // Local state for time inputs - allows free typing without immediate conversion
  // Key is `${index}-${field}`, value is the display string
  const [timeInputs, setTimeInputs] = useState({});

  // Helper to get display key for time inputs
  const getTimeKey = (index, field) => `${index}-${field}`;

  // Format UTC time from storage to local display string
  const formatTimeForDisplay = useCallback((utcTime) => {
    if (!utcTime) return '';
    const local = utcToLocal(utcTime, timezone);
    return local ? local.format('YYYY-MM-DD HH:mm') : '';
  }, [timezone]);

  // Sync local timeInputs state when stops change from external source (e.g., estimate times)
  useEffect(() => {
    const newTimeInputs = {};
    stops.forEach((stop, index) => {
      const arrivalKey = getTimeKey(index, 'arrival_time');
      const departureKey = getTimeKey(index, 'departure_time');
      // Only set if we don't have a local value or the stored value changed
      if (stop.arrival_time) {
        newTimeInputs[arrivalKey] = formatTimeForDisplay(stop.arrival_time);
      }
      if (stop.departure_time) {
        newTimeInputs[departureKey] = formatTimeForDisplay(stop.departure_time);
      }
    });
    setTimeInputs(newTimeInputs);
  }, [stops, formatTimeForDisplay]);

  // Use a ref to track pending updates to avoid race conditions
  // when both address and coordinates are updated simultaneously
  const pendingStopsRef = useRef(null);

  // Flush pending updates
  const flushPendingUpdates = useCallback(() => {
    if (pendingStopsRef.current) {
      onChange(pendingStopsRef.current);
      pendingStopsRef.current = null;
    }
  }, [onChange]);

  const addStop = () => {
    const newStop = {
      order: stops.length + 1,
      address: '',
      latitude: null,
      longitude: null,
      arrival_time: null,
      departure_time: null,
      duration_minutes: null,
      stop_type: 'break',
      notes: '',
    };
    onChange([...stops, newStop]);
  };

  const removeStop = (index) => {
    const newStops = stops.filter((_, i) => i !== index);
    // Reorder after removal
    const reordered = newStops.map((stop, i) => ({ ...stop, order: i + 1 }));
    onChange(reordered);
  };

  const updateStop = (index, field, value) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], [field]: value };
    onChange(newStops);
  };

  const handleAddressChange = (index, address) => {
    // Use pending ref to accumulate changes (address + coordinates come together)
    const latestStops = pendingStopsRef.current || [...stops];
    latestStops[index] = { ...latestStops[index], address };
    pendingStopsRef.current = latestStops;
    // Schedule flush for next tick to batch with coordinates update
    setTimeout(flushPendingUpdates, 0);
  };

  const handleCoordinatesChange = (index, { lat, lon }) => {
    // Use pending ref to accumulate changes (address + coordinates come together)
    const latestStops = pendingStopsRef.current || [...stops];
    latestStops[index] = {
      ...latestStops[index],
      latitude: lat != null ? Number(lat) : null,
      longitude: lon != null ? Number(lon) : null,
    };
    pendingStopsRef.current = latestStops;
    // Schedule flush for next tick to batch with address update
    setTimeout(flushPendingUpdates, 0);
  };

  // Handle time change - store raw value in local state (like OrderInputForm does)
  // Conversion to UTC happens on blur or when picker selects a date
  const handleTimeChange = (index, field, value) => {
    const key = getTimeKey(index, field);

    // react-datetime passes moment objects or strings
    if (!value || value === '') {
      setTimeInputs((prev) => ({ ...prev, [key]: '' }));
      updateStop(index, field, null);
      return;
    }

    // If it's a moment object from picker selection, convert immediately and save
    if (typeof value === 'object' && value.isValid && value.isValid()) {
      const formatted = value.format('YYYY-MM-DD HH:mm');
      setTimeInputs((prev) => ({ ...prev, [key]: formatted }));
      updateStop(index, field, localToUtc(value, timezone));
    } else {
      // For string input (typing), just store as-is - don't convert yet
      setTimeInputs((prev) => ({ ...prev, [key]: value }));
    }
  };

  // Handle blur - convert typed value to UTC when user leaves the input
  const handleTimeBlur = (index, field) => {
    const key = getTimeKey(index, field);
    const value = timeInputs[key];

    if (!value || value === '') {
      updateStop(index, field, null);
      return;
    }

    // Try to parse the typed value
    const parsed = dayjs(value, 'YYYY-MM-DD HH:mm', true);
    if (parsed.isValid()) {
      updateStop(index, field, localToUtc(value, timezone));
    }
    // If invalid, the value stays in local state but isn't saved to stop
  };

  const moveStop = (index, direction) => {
    if (
      (direction === -1 && index === 0)
      || (direction === 1 && index === stops.length - 1)
    ) {
      return;
    }
    const newStops = [...stops];
    const targetIndex = index + direction;
    [newStops[index], newStops[targetIndex]] = [newStops[targetIndex], newStops[index]];
    // Update order numbers
    const reordered = newStops.map((stop, i) => ({ ...stop, order: i + 1 }));
    onChange(reordered);
  };

  // Estimate arrival/departure times based on travel time and duration at each stop
  const estimateTimes = async () => {
    setIsEstimating(true);
    try {
      const newStops = [...stops];
      // Start from booking datetime - a local datetime string from the picker
      // Parse it in the booking's timezone for calculations
      let currentTime = startDateTime
        ? dayjs.tz(startDateTime, timezone)
        : dayjs().tz(timezone);
      let prevCoords = fromCoords;

      for (let i = 0; i < newStops.length; i += 1) {
        const stop = newStops[i];

        // Calculate travel time from previous location to this stop
        let travelDuration = null;
        let travelDistance = null;
        if (stop.latitude && stop.longitude && prevCoords?.lat && prevCoords?.lon) {
          // eslint-disable-next-line no-await-in-loop
          const route = await getRoute(prevCoords, { lat: stop.latitude, lon: stop.longitude });
          if (route?.duration) {
            // Add travel time (duration is in minutes)
            currentTime = currentTime.add(route.duration, 'minute');
            travelDuration = route.duration;
            travelDistance = route.distance;
          }
        }

        // Set arrival time and travel info - store as UTC
        newStops[i] = {
          ...newStops[i],
          arrival_time: currentTime.utc().toISOString(),
          travel_duration_minutes: travelDuration,
          travel_distance_km: travelDistance,
        };

        // Add duration at stop to get departure time
        const durationMinutes = stop.duration_minutes || 0;
        if (durationMinutes > 0) {
          currentTime = currentTime.add(durationMinutes, 'minute');
        }

        // Set departure time - store as UTC
        newStops[i] = {
          ...newStops[i],
          departure_time: currentTime.utc().toISOString(),
        };

        // Update previous coords for next iteration
        if (stop.latitude && stop.longitude) {
          prevCoords = { lat: stop.latitude, lon: stop.longitude };
        }
      }

      onChange(newStops);
    } finally {
      setIsEstimating(false);
    }
  };

  // Check if we have all info for accurate estimates (travel time calculation)
  const hasFullEstimateInfo = startDateTime && fromCoords?.lat && fromCoords?.lon;
  // Button enabled only when date/time is set and there are stops
  const canEstimate = stops.length > 0 && startDateTime;

  return (
    <div className="itinerary-stops-editor">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i className="fa fa-map-signs me-2" />
          Itinerary Stops
          {stops.length > 0 && (
            <Badge bg="secondary" className="ms-2">{stops.length}</Badge>
          )}
        </h6>
        <div className="d-flex gap-2">
          {canEstimate && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={estimateTimes}
              disabled={disabled || isEstimating}
              title={hasFullEstimateInfo
                ? 'Calculate estimated arrival/departure times based on travel time'
                : 'Set booking date/time and pickup address to calculate travel times'}
            >
              {isEstimating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Estimating...
                </>
              ) : (
                <>
                  <i className="fa fa-clock me-1" />
                  Estimate Times
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={addStop}
            disabled={disabled}
          >
            <i className="fa fa-plus me-1" />
            Add Stop
          </Button>
        </div>
      </div>

      {stops.length === 0 && (
        <div className="text-muted text-center py-3 border rounded bg-light">
          <i className="fa fa-route fa-2x mb-2 d-block opacity-50" />
          No intermediate stops. Click &quot;Add Stop&quot; to add waypoints.
        </div>
      )}

      {stops.map((stop, index) => (
        <Card key={`stop-${stop.order || index}`} className="mb-3">
          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center gap-2">
                <Badge bg="secondary" className="rounded-circle">
                  {index + 1}
                </Badge>
                <Form.Select
                  size="sm"
                  value={stop.stop_type || 'waypoint'}
                  onChange={(e) => updateStop(index, 'stop_type', e.target.value)}
                  disabled={disabled}
                  style={{ width: 'auto' }}
                >
                  {STOP_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Badge bg={STOP_TYPES.find((t) => t.value === stop.stop_type)?.color || 'secondary'}>
                  {STOP_TYPES.find((t) => t.value === stop.stop_type)?.label || stop.stop_type}
                </Badge>
              </div>
              <div className="d-flex gap-1">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => moveStop(index, -1)}
                  disabled={disabled || index === 0}
                  title="Move up"
                >
                  <i className="fa fa-arrow-up" />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => moveStop(index, 1)}
                  disabled={disabled || index === stops.length - 1}
                  title="Move down"
                >
                  <i className="fa fa-arrow-down" />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeStop(index)}
                  disabled={disabled}
                  title="Remove stop"
                >
                  <i className="fa fa-trash" />
                </Button>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">Address</Form.Label>
                  <AddressAutocomplete
                    id={`stop-${index}-address`}
                    value={stop.address || ''}
                    onChange={(value) => handleAddressChange(index, value)}
                    onCoordinatesChange={(coords) => handleCoordinatesChange(index, coords)}
                    placeholder="Enter stop address..."
                    disabled={disabled}
                  />
                  {stop.latitude && stop.longitude && (
                    <Form.Text className="text-success">
                      <i className="fa fa-check-circle me-1" />
                      Coordinates set
                    </Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-4 col-6">
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">Arrival Time</Form.Label>
                  <Datetime
                    value={timeInputs[getTimeKey(index, 'arrival_time')] ?? formatTimeForDisplay(stop.arrival_time)}
                    onChange={(value) => handleTimeChange(index, 'arrival_time', value)}
                    dateFormat="YYYY-MM-DD"
                    timeFormat="HH:mm"
                    inputProps={{
                      className: 'form-control form-control-sm',
                      placeholder: 'Arrival',
                      disabled,
                      onBlur: () => handleTimeBlur(index, 'arrival_time'),
                    }}
                    closeOnSelect
                  />
                  {stop.travel_duration_minutes && (
                    <Form.Text className="text-muted">
                      <i className="fa fa-car me-1" />
                      {stop.travel_duration_minutes}
                      {' '}
                      min
                      {stop.travel_distance_km && ` (${stop.travel_distance_km} km)`}
                      {' '}
                      from previous
                    </Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-4 col-6">
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">Departure Time</Form.Label>
                  <Datetime
                    value={timeInputs[getTimeKey(index, 'departure_time')] ?? formatTimeForDisplay(stop.departure_time)}
                    onChange={(value) => handleTimeChange(index, 'departure_time', value)}
                    dateFormat="YYYY-MM-DD"
                    timeFormat="HH:mm"
                    inputProps={{
                      className: 'form-control form-control-sm',
                      placeholder: 'Departure',
                      disabled,
                      onBlur: () => handleTimeBlur(index, 'departure_time'),
                    }}
                    closeOnSelect
                  />
                </Form.Group>
              </div>

              <div className="col-md-4 col-12">
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">Duration (min)</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={stop.duration_minutes || ''}
                    onChange={(e) => updateStop(index, 'duration_minutes', e.target.value ? Number(e.target.value) : null)}
                    placeholder="Minutes"
                    min={0}
                    disabled={disabled}
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label className="small text-muted mb-1">Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    size="sm"
                    value={stop.notes || ''}
                    onChange={(e) => updateStop(index, 'notes', e.target.value)}
                    placeholder="Special instructions for this stop..."
                    disabled={disabled}
                  />
                </Form.Group>
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

export default ItineraryStopsEditor;
